import { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import Button from 'components/stateless/Button/Button';
import Pagination from 'components/stateless/Pagination/Pagination';
import StepWizard from 'components/stateless/StepWizard/StepWizard';
import TableBody from 'components/stateless/Table/TableBody';
import TableBorder from 'components/stateless/Table/TableBorder';
import type { HeaderType } from 'components/stateless/Table/TableHeader';
import TableHeader from 'components/stateless/Table/TableHeader';
import Td from 'components/stateless/Table/Td';
import Tr from 'components/stateless/Table/Tr';
import { HeaderTitle } from 'components/stateless/Title/HeaderTitle';
import { SectionTitle } from 'components/stateless/Title/SectionTitle';
import { ROUTES_FI } from 'constants/routes/financier';
import useMounted from 'hooks/useMounted';
import usePageable from 'hooks/usePageable';
import type Pageable from 'models/Pageable';
import type { DealerAgreementVOModel } from 'models/vo/DealerAgreementVO';
import { requestTempFinancierDealerAgreementList } from 'utils/http/api/financier/dealer-agreements';
import useModal from 'utils/modal/useModal';

function FinancierInvoiceRegisterStep1() {
  const history = useHistory();
  const mounted = useMounted();
  const modal = useModal();
  const { t } = useTranslation(['format']);
  const [dealerAgreementPageData, setDealerAgreementPageData] = useState<Pageable<DealerAgreementVOModel[]>>();
  const [searchDealerName, setSearchDealerName] = useState<string>('');
  const { pageable, setPageable } = usePageable();

  useEffect(() => {
    if (mounted) {
      fetchAll();
    }
  }, [mounted]);

  const fetchAll = async (selectedPageNumber: number = 1, selectedRowNumber: number = 10): Promise<void> => {
    try {
      const dealerAgreementPageResponse = await requestTempFinancierDealerAgreementList(
        selectedPageNumber,
        selectedRowNumber,
        {
          dealerName: searchDealerName,
        },
      );

      ReactDOM.unstable_batchedUpdates(() => {
        setDealerAgreementPageData(dealerAgreementPageResponse);
        setPageable(dealerAgreementPageResponse);
      });
    } catch (error) {
      modal.show(error);
    }
  };

  const onChangeSearchInput = (e: any) => {
    setSearchDealerName(e.target.value);
  };

  const onClickSearch = async (e: any) => {
    e.preventDefault();

    try {
      const dealerAgreementPage = await requestTempFinancierDealerAgreementList(1, pageable.sizePerPage, {
        dealerName: searchDealerName,
      });

      ReactDOM.unstable_batchedUpdates(() => {
        setDealerAgreementPageData(dealerAgreementPage);
        setPageable(dealerAgreementPage);
      });
    } catch (error) {
      modal.show(error);
    }
  };

  const paginate = (selectedPageNumber: number, selectedRowNumber: number): void => {
    fetchAll(selectedPageNumber, selectedRowNumber);
  };

  const routeDetailPage = (e: any, item: DealerAgreementVOModel): void => {
    e.preventDefault();

    history.push(ROUTES_FI.REGISTER_INVOICE.REGISTRATION_STEP2_BUILD_PATH(item.dealerAgreementId, item.financierId));
  };

  const renderDealerAgreementTable = (): JSX.Element[] | JSX.Element | undefined => {
    return dealerAgreementPageData?.content.map((dealerAgreementData, index: number) => (
      <Tr key={index}>
        <Td data={dealerAgreementData.dealerFinancierClientName} />
        <Td data={dealerAgreementData.dealerFinancierClientCode} />
        <Td data={dealerAgreementData.contractNo} />
        <Td data={dealerAgreementData.anchorAgreementContractNo} />
        <Td data={dealerAgreementData.anchorFinancierClientName} />
        <Td data={dealerAgreementData.currencyType} />
        <Td data={dealerAgreementData.dealerFinancierClientAddress} />
        <Td data={dealerAgreementData.dealerFinancierClientRepresentativeName} />
        <Td className="text-center">
          <Button onClick={e => routeDetailPage(e, dealerAgreementData)}>{t('text:Select')}</Button>
        </Td>
      </Tr>
    ));
  };

  const tableHeaders: HeaderType[] = [
    {
      headerText: t('text:Partner_Name'),
      colWidths: 120,
    },
    {
      headerText: t('text:Partner_Client_Code'),
      colWidths: 120,
    },
    {
      headerText: t('text:Partner_Master_Agreement_Number'),
      colWidths: 120,
    },
    {
      headerText: t('text:Associated_Anchor_Master_Agreement'),
      colWidths: 120,
    },
    {
      headerText: t('text:Associated_Anchor_Name'),
      colWidths: 120,
    },
    {
      headerText: t('text:Currency'),
      colWidths: 120,
    },
    {
      headerText: t('text:Registered_Office_Address'),
      colWidths: 180,
    },
    {
      headerText: t('text:Legal_Representative_Name'),
      colWidths: 150,
    },
    {
      headerText: '',
      colWidths: 80,
    },
  ];

  return (
    <>
      <HeaderTitle title={t('text:Invoice_Registration')} />
      <div className="content-area">
        <StepWizard nth={1} title={[t('text:Select_Partner'), t('text:Enter_Invoice_Information_&_Register')]} />
      </div>

      <div className="content-area">
        <SectionTitle title={t('text:Partner_List')} />
        <form>
          <div className="d-flex information-filter mt-0">
            <input
              className="only-input"
              type="text"
              placeholder={t('text:Partner_Name')}
              onChange={onChangeSearchInput}
            />
            <Button onClick={onClickSearch}>{t('text:Search')}</Button>
          </div>
        </form>
        <TableBorder>
          <TableHeader header={tableHeaders} />
          <TableBody numOfCol={tableHeaders.length}>{renderDealerAgreementTable()}</TableBody>
        </TableBorder>
        <Pagination pageable={pageable} paginate={paginate} />
      </div>
    </>
  );
}

export default FinancierInvoiceRegisterStep1;
